import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import orderService from '../../../services/orderService';
import '../../../pages/Pages.css';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [perPage] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    search: '',
    per_page: perPage
  });

  // Edit form data
  const [editFormData, setEditFormData] = useState({
    notes: '',
    payment_method: '',
    tender_amount: ''
  });

  // Status form data
  const [statusFormData, setStatusFormData] = useState({
    status: ''
  });

  useEffect(() => {
    loadOrders();
  }, [currentPage, filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getAllOrders({
        ...filters,
        page: currentPage,
        per_page: perPage
      });
      
      setOrders(response.data.data);
      setTotalPages(response.data.last_page);
      setTotalOrders(response.data.total);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await orderService.getOrderById(order.id);
      setSelectedOrder(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load order details', 'error');
    }
  };

  const handleEditOrder = async (order) => {
    try {
      const response = await orderService.getOrderById(order.id);
      setSelectedOrder(response.data);
      setEditFormData({
        notes: response.data.notes || '',
        payment_method: response.data.payment_method,
        tender_amount: response.data.tender_amount || ''
      });
      setShowEditModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load order details', 'error');
    }
  };

  const handleStatusChange = (order) => {
    setSelectedOrder(order);
    setStatusFormData({
      status: order.status
    });
    setShowStatusModal(true);
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        ...editFormData,
        tender_amount: editFormData.tender_amount ? parseFloat(editFormData.tender_amount) : null
      };

      await orderService.updateOrder(selectedOrder.id, formData);
      Swal.fire('Success!', 'Order updated successfully', 'success');
      setShowEditModal(false);
      loadOrders();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update order', 'error');
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      await orderService.updateOrderStatus(selectedOrder.id, statusFormData.status);
      Swal.fire('Success!', 'Order status updated successfully', 'success');
      setShowStatusModal(false);
      loadOrders();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update order status', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      payment_method: '',
      search: '',
      per_page: perPage
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'completed';
      case 'pending': return 'pending';
      case 'processing': return 'processing';
      case 'cancelled': return 'cancelled';
      case 'refunded': return 'refunded';
      default: return 'pending';
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash': return '💵 Cash';
      case 'card': return '💳 Card';
      case 'digital_wallet': return '📱 Digital Wallet';
      case 'bank_transfer': return '🏦 Bank Transfer';
      default: return method;
    }
  };

  // Pagination controls
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ««
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          «
        </button>
        
        {startPage > 1 && (
          <>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pages}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          »
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          »»
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Cashier Order Management</h1>
          <p>Manage and track customer orders</p>
        </div>
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>Cashier Management</h1>
            <p>
              Manage and track customer orders 
              {totalOrders > 0 && ` (${totalOrders} total orders, showing ${orders.length} on this page)`}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={loadOrders}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section single-row">
        <div className="filters-row">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search by order number..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="filter-select"
            >
              <option value="">All Payment Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="digital_wallet">Digital Wallet</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          <div className="filter-actions">
            <button className="btn btn-clear" onClick={clearFilters} title="Clear Filters">
              🗑️ Clear
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Cashier</th>
                <th>Items</th>
                <th>Total Amount</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td className="order-number">
                    <strong>{order.order_number}</strong>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {order.cashier ? (
                      <div className="cashier-info">
                        <span className="cashier-name">{order.cashier.full_legal_name}</span>
                        <span className="cashier-id">({order.cashier.employee_id})</span>
                      </div>
                    ) : (
                      <span className="no-cashier">N/A</span>
                    )}
                  </td>
                  <td>
                    <div className="items-count">
                      {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                    </div>
                    <div className="items-preview">
                      {order.items.slice(0, 2).map(item => (
                        <span key={item.id} className="item-name">
                          {item.product_name}
                          {item.variant_name && ` (${item.variant_name})`}
                        </span>
                      ))}
                      {order.items.length > 2 && (
                        <span className="more-items">+{order.items.length - 2} more</span>
                      )}
                    </div>
                  </td>
                  <td className="amount-cell">
                    <strong>{formatCurrency(order.total_amount)}</strong>
                    {order.tender_amount && (
                      <div className="tender-info">
                        Tender: {formatCurrency(order.tender_amount)}
                      </div>
                    )}
                    {order.change_amount && order.change_amount > 0 && (
                      <div className="change-info">
                        Change: {formatCurrency(order.change_amount)}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="payment-method">
                      {getPaymentMethodLabel(order.payment_method)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-view"
                        onClick={() => handleViewOrder(order)}
                        title="View Order Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="btn btn-edit"
                        onClick={() => handleEditOrder(order)}
                        title="Edit Order"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn btn-status"
                        onClick={() => handleStatusChange(order)}
                        title="Change Status"
                      >
                        🔄
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {orders.length === 0 && (
            <div className="no-data">
              <div className="no-data-icon">📦</div>
              <h3>No Orders Found</h3>
              <p>
                {filters.status || filters.payment_method || filters.search 
                  ? 'No orders match your current filters.' 
                  : 'No orders have been placed yet.'
                }
              </p>
              {filters.status || filters.payment_method || filters.search ? (
                <button className="btn btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && renderPagination()}
      </div>

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <ViewOrderModal
          order={selectedOrder}
          onClose={() => setShowViewModal(false)}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusBadgeClass={getStatusBadgeClass}
          getPaymentMethodLabel={getPaymentMethodLabel}
        />
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && (
        <EditOrderModal
          order={selectedOrder}
          formData={editFormData}
          setFormData={setEditFormData}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateOrder}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && (
        <StatusChangeModal
          order={selectedOrder}
          formData={statusFormData}
          setFormData={setStatusFormData}
          onClose={() => setShowStatusModal(false)}
          onSubmit={handleUpdateStatus}
          getStatusBadgeClass={getStatusBadgeClass}
        />
      )}
    </div>
  );
};

// View Order Modal Component
const ViewOrderModal = ({ order, onClose, formatDate, formatCurrency, getStatusBadgeClass, getPaymentMethodLabel }) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h3>Order Details - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="order-detail-view">
            {/* Order Header */}
            <div className="order-header">
              <div className="order-meta">
                <div className="meta-item">
                  <label>Order Number:</label>
                  <span>{order.order_number}</span>
                </div>
                <div className="meta-item">
                  <label>Order Date:</label>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div className="meta-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                    {order.status}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Payment Method:</label>
                  <span>{getPaymentMethodLabel(order.payment_method)}</span>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h4>Order Summary</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <label>Subtotal:</label>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                <div className="summary-item">
                  <label>Tax:</label>
                  <span>{formatCurrency(order.tax_amount)}</span>
                </div>
                <div className="summary-item">
                  <label>Discount:</label>
                  <span>-{formatCurrency(order.discount_amount)}</span>
                </div>
                <div className="summary-item total">
                  <label>Total Amount:</label>
                  <span>{formatCurrency(order.total_amount)}</span>
                </div>
                {order.tender_amount && (
                  <div className="summary-item">
                    <label>Tender Amount:</label>
                    <span>{formatCurrency(order.tender_amount)}</span>
                  </div>
                )}
                {order.change_amount && order.change_amount > 0 && (
                  <div className="summary-item">
                    <label>Change Given:</label>
                    <span>{formatCurrency(order.change_amount)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items">
              <h4>Order Items ({order.items.length})</h4>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                      <th>Barcode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="product-info">
                            <strong>{item.product_name}</strong>
                            {item.product?.brand && (
                              <span className="product-brand">{item.product.brand}</span>
                            )}
                          </div>
                        </td>
                        <td>{item.variant_name || 'N/A'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{formatCurrency(item.total_price)}</td>
                        <td>
                          <div className="barcode-info">
                            <code>{item.barcode}</code>
                            {item.barcodes_used && item.barcodes_used.length > 0 && (
                              <div className="barcodes-used">
                                {item.barcodes_used.map((barcode, index) => (
                                  <small key={index}>{barcode}</small>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cashier Information */}
            {order.cashier && (
              <div className="cashier-details">
                <h4>Cashier Information</h4>
                <div className="cashier-card">
                  <div className="cashier-avatar">
                    {order.cashier.full_legal_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div className="cashier-info">
                    <h5>{order.cashier.full_legal_name}</h5>
                    <p>Employee ID: {order.cashier.employee_id}</p>
                    <p>Role: {order.cashier.user_role}</p>
                    {order.cashier.is_online && (
                      <span className="online-status">🟢 Online</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Order Notes */}
            {order.notes && (
              <div className="order-notes">
                <h4>Order Notes</h4>
                <div className="notes-content">
                  {order.notes}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Order Modal Component
const EditOrderModal = ({ order, formData, setFormData, onClose, onSubmit, formatCurrency }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Order - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="edit-order-form">
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="digital_wallet">Digital Wallet</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tender Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="tender_amount"
                  value={formData.tender_amount}
                  onChange={handleInputChange}
                  placeholder="Enter tender amount"
                />
                <small>Current total: {formatCurrency(order.total_amount)}</small>
              </div>

              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter any order notes..."
                />
              </div>

              <div className="current-info">
                <h5>Current Order Information</h5>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Total Amount:</label>
                    <span>{formatCurrency(order.total_amount)}</span>
                  </div>
                  <div className="info-item">
                    <label>Current Payment Method:</label>
                    <span>{order.payment_method}</span>
                  </div>
                  <div className="info-item">
                    <label>Current Tender Amount:</label>
                    <span>{formatCurrency(order.tender_amount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Status Change Modal Component
const StatusChangeModal = ({ order, formData, setFormData, onClose, onSubmit, getStatusBadgeClass }) => {
  const handleStatusChange = (e) => {
    setFormData({ status: e.target.value });
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'Order has been placed but not processed' },
    { value: 'processing', label: 'Processing', description: 'Order is being prepared' },
    { value: 'completed', label: 'Completed', description: 'Order has been fulfilled' },
    { value: 'cancelled', label: 'Cancelled', description: 'Order has been cancelled' },
    { value: 'refunded', label: 'Refunded', description: 'Order has been refunded' }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Change Order Status - {order.order_number}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="status-change-form">
              <div className="current-status">
                <label>Current Status:</label>
                <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                  {order.status}
                </span>
              </div>

              <div className="form-group">
                <label>New Status *</label>
                <select
                  value={formData.status}
                  onChange={handleStatusChange}
                  required
                >
                  <option value="">Select new status</option>
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.status && (
                <div className="status-description">
                  <strong>
                    {statusOptions.find(opt => opt.value === formData.status)?.label}
                  </strong>
                  <p>{statusOptions.find(opt => opt.value === formData.status)?.description}</p>
                </div>
              )}

              <div className="status-warning">
                <p>⚠️ Changing order status may affect inventory and reporting.</p>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Orders;